import { useEffect } from 'react';

/** Separator between the page context and the app/brand name (e.g. `Vehicles | Hetivo`). */
const BRAND_SEPARATOR = ' | ';

/** Separator between page-context segments (e.g. `Insurance – AB-123-CD`). */
const SEGMENT_SEPARATOR = ' – ';

/**
 * Builds a browser tab title from a base (brand) name and zero or more context
 * segments, ordered most-specific first.
 *
 * The brand name always comes last so the page-specific part stays visible when
 * the browser truncates narrow tabs from the right.
 *
 * @example
 * formatDocumentTitle('Hetivo', ['Vehicles'])                       // 'Vehicles | Hetivo'
 * formatDocumentTitle('Hetivo', ['AB-123-CD'])                      // 'AB-123-CD | Hetivo'
 * formatDocumentTitle('Hetivo', ['Insurance', 'AB-123-CD'])         // 'Insurance – AB-123-CD | Hetivo'
 * formatDocumentTitle('Hetivo', [])                                 // 'Hetivo'
 */
export function formatDocumentTitle(base: string, segments: ReadonlyArray<string | null | undefined> = []): string {
  const prefix = segments
    .filter((segment): segment is string => Boolean(segment && segment.trim()))
    .join(SEGMENT_SEPARATOR);

  return prefix ? `${prefix}${BRAND_SEPARATOR}${base}` : base;
}

/**
 * Sets `document.title` to the given value.
 *
 * A nullish or empty title is a no-op: it leaves the current title untouched.
 * This lets a centralized caller (e.g. the root layout) defer to a more specific
 * writer for routes that manage their own title — without clobbering it.
 */
export function useDocumentTitle(title: string | null | undefined): void {
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
}
